import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { useState } from 'react';
import { BLOCK_DEFS } from './blockSchema.js';
import { useEditor } from './editorStore.js';

// The Layers panel is a navigable + sortable outline of the page tree.
// Each row is sortable via `useSortable` with id `layer-{block.id}` (distinct
// from canvas SortableBlockNode ids so dnd-kit doesn't treat them as the
// same item). Drags from layers carry data.layer = true so the editor's
// collision detection can keep layer drags inside the layer panel.
export default function LayersPanel() {
  const blocks = useEditor((s) => s.blocks);

  if (blocks.length === 0) {
    return (
      <div className="px-4 py-6 text-xs text-ink-500">
        No blocks on this page yet. Add some from the Blocks tab or use ✨ AI.
      </div>
    );
  }

  return (
    <ul className="select-none py-1">
      <LayerLevel blocks={blocks} parentId="__root__" depth={0} />
    </ul>
  );
}

function LayerLevel({ blocks, parentId, depth }) {
  return (
    <SortableContext
      items={blocks.map((b) => `layer-${b.id}`)}
      strategy={verticalListSortingStrategy}
    >
      {blocks.map((b, i) => (
        <LayerRow
          key={b.id}
          block={b}
          parentId={parentId}
          index={i}
          depth={depth}
        />
      ))}
    </SortableContext>
  );
}

function LayerRow({ block, parentId, index, depth }) {
  const def = BLOCK_DEFS[block.type];
  const hasChildren = Array.isArray(block.children) && block.children.length > 0;
  const [open, setOpen] = useState(true);

  const selectedId = useEditor((s) => s.selectedId);
  const select = useEditor((s) => s.select);
  const isSelected = selectedId === block.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
  } = useSortable({
    id: `layer-${block.id}`,
    data: {
      source: 'block',
      parentId,
      index,
      type: block.type,
      blockId: block.id,
      layer: true,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const showInsertLine = isOver && active && active.id !== `layer-${block.id}`;

  function rowClick(e) {
    e.stopPropagation();
    select(block.id);
    // Bring the corresponding canvas block into view. Lookup by the
    // data-block-id attribute we set on each SortableBlockNode wrapper.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-block-id="${block.id}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  function toggle(e) {
    e.stopPropagation();
    setOpen((v) => !v);
  }

  return (
    <li ref={setNodeRef} style={style} className={clsx('relative', isDragging && 'opacity-40')}>
      {showInsertLine && (
        <div
          aria-hidden
          className="pointer-events-none absolute -top-px left-0 right-0 z-10 h-0.5 rounded-full bg-brand-500"
        />
      )}
      <div
        className={clsx(
          'flex items-center gap-1 px-2 py-1 text-xs transition',
          isSelected
            ? 'bg-brand-50 text-brand-700 dark:bg-ink-800 dark:text-ink-50'
            : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800',
        )}
        style={{ paddingLeft: 4 + depth * 14 }}
      >
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="grid h-5 w-3 cursor-grab place-items-center text-ink-400 hover:text-ink-600 active:cursor-grabbing"
          title="Drag to reorder"
          onClick={(e) => e.stopPropagation()}
        >
          ⠿
        </button>
        {hasChildren ? (
          <button
            type="button"
            onClick={toggle}
            className="grid h-4 w-4 place-items-center rounded text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700"
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="inline-block w-4" />
        )}
        <button
          type="button"
          onClick={rowClick}
          className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-left"
        >
          <span className="w-4 text-center text-ink-400">{def?.icon ?? '?'}</span>
          <span className="truncate">{labelFor(block, def)}</span>
        </button>
      </div>
      {hasChildren && open && (
        <ul>
          <LayerLevel blocks={block.children} parentId={block.id} depth={depth + 1} />
        </ul>
      )}
    </li>
  );
}

// Show a content-aware label so two text blocks aren't both just "Text".
function labelFor(block, def) {
  const base = def?.label ?? block.type;
  const props = block.props || {};
  const summary = props.text ?? props.label ?? props.brand ?? props.heading;
  if (typeof summary === 'string' && summary.trim()) {
    const stripped = summary.replace(/<[^>]*>/g, '').trim();
    if (stripped) return `${base} · ${truncate(stripped, 22)}`;
  }
  return base;
}

function truncate(s, n) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
